import React from "react"
import { useWhatsAppAccess } from "../../core/utils/hooks/useWhatsAppAccess"
import { ModalBody, ModalHeader, ModalPanel } from "../Modal"
import { Button, ButtonLoader } from "../Button"
import { Spinner } from "../Loading"
import ChatIcon from "../../assets/icons/chat.svg"
import SuccessIcon from "../../assets/icons/success.svg"
import "./styles.scss"

export const WhatsAppGate = ({ children }) => {
  const {
    connectionStatus,
    isConnected,
    isLoading,
    hasAccess,
    qrCode,
    generateQR,
    refreshStatus,
    instanceName,
    error,
    clearError
  } = useWhatsAppAccess()

  // If connected, render children (the chat)
  if (hasAccess) {
    return children
  }

  // If loading initial check, show loading
  if (isLoading && connectionStatus === 'checking') {
    return (
      <div className="whatsapp-gate">
        <div className="whatsapp-gate__loading">
          <div className="loading-animation">
            <Spinner size="large" color="primary" />
            <div className="loading-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
          <h3>🔍 Verificando sua conexão...</h3>
          <p>Estamos checando se seu WhatsApp já está conectado. Isso levará apenas alguns segundos!</p>
          <div className="loading-tips">
            <small>💡 <strong>Dica:</strong> Mantenha seu celular por perto para uma conexão mais rápida</small>
          </div>
        </div>
      </div>
    )
  }

  // Render gate modal
  return (
    <ModalPanel modalIsOpen={true} shouldCloseOnOverlayClick={false}>
      <ModalHeader 
        title="🚀 Integre seu WhatsApp com a PlayBPO" 
        line={false}
      />
      <ModalBody className="whatsapp-gate-modal">
        <div className="whatsapp-gate-modal__content">
          
          {/* Main Layout Container */}
          <div className="whatsapp-gate-modal__main-layout">
            
            {/* Left Section - PlayBPO Information */}
            <div className="whatsapp-gate-modal__info-section">
              <div className="info-header">
                <h2 className="info-title">Por que integrar?</h2>
                <p className="info-description">
                  Conecte seu número do WhatsApp à plataforma e agilize a comunicação com seus clientes de forma profissional e organizada.
                </p>
              </div>
              
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">📱</div>
                  <div className="benefit-content">
                    <h4>Recepção centralizada</h4>
                    <p>Receba mensagens de clientes diretamente na PlayBPO</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🔄</div>
                  <div className="benefit-content">
                    <h4>Transformação automática</h4>
                    <p>Converta conversas e evidências em comunicações organizadas</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📋</div>
                  <div className="benefit-content">
                    <h4>Histórico completo</h4>
                    <p>Acompanhe atendimentos e respostas em um só lugar</p>
                  </div>
                </div>
              </div>
              
              <div className="security-info">
                <div className="security-icon">🔒</div>
                <div className="security-content">
                  <p><strong>Dados seguros</strong></p>
                  <p>Integração que segue os padrões de privacidade e segurança da PlayBPO</p>
                </div>
              </div>
            </div>

            {/* Right Section - Instructions and Actions */}
            <div className="whatsapp-gate-modal__action-section">
              
              {error && (
                <div className="error-message">
                  <div className="error-header">
                    <div className="error-icon">😓</div>
                    <h4>Ops! Algo não deu certo</h4>
                  </div>
                  <div className="error-content">
                    <p className="error-description">
                      Não se preocupe, isso pode acontecer! Vamos tentar novamente juntos.
                    </p>
                    <div className="error-details">
                      <small><strong>Detalhes técnicos:</strong> {error}</small>
                    </div>
                  </div>
                  <div className="error-actions">
                    <Button 
                      label="🔄 Vamos tentar de novo" 
                      handleClick={() => {
                        clearError()
                        refreshStatus()
                      }}
                      color="primary"
                    />
                    <div className="error-help">
                      <small>💬 <strong>Precisa de ajuda?</strong> Nossa equipe está sempre disponível para te auxiliar</small>
                    </div>
                  </div>
                </div>
              )}

              {connectionStatus === 'disconnected' && !qrCode && (
                <div className="connect-step">
                  <div className="step-header">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h3>Iniciar Conexão</h3>
                      <p>Clique no botão abaixo para gerar o QR Code e conectar seu WhatsApp</p>
                    </div>
                  </div>
                  
                  <div className="instance-info">
                    <p><span className="instance-label">Instância:</span> {instanceName}</p>
                  </div>
                  
                  <div className="action-container">
                    <ButtonLoader
                      loading={isLoading}
                      messageProcess="🎯 Criando sua conexão especial..."
                      label="✨ Conectar meu WhatsApp"
                      handleClick={generateQR}
                      color="success"
                    />
                    <div className="connection-encouragement">
                      <small>🚀 <strong>Quase lá!</strong> Em poucos cliques você terá tudo integrado</small>
                    </div>
                  </div>
                </div>
              )}

              {(connectionStatus === 'generating_qr' && qrCode) && (
                <div className="qr-step">
                  <div className="step-header">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h3>Escaneie o QR Code</h3>
                      <p>Abra o WhatsApp no seu celular e siga os passos:</p>
                    </div>
                  </div>
                  
                  <div className="qr-instructions">
                    <ol className="instruction-list">
                      <li>Abra o <strong>WhatsApp</strong> no seu celular</li>
                      <li>Vá em <strong>Configurações</strong> (⚙️)</li>
                      <li>Toque em <strong>Dispositivos conectados</strong></li>
                      <li>Selecione <strong>Conectar dispositivo</strong></li>
                      <li>Escaneie o QR Code abaixo</li>
                    </ol>
                  </div>
                  
                  <div className="qr-code-container">
                    <div className="qr-code-wrapper">
                      <img src={qrCode} alt="QR Code WhatsApp" className="qr-code-image" />
                    </div>
                    <p className="qr-code-label">QR Code para conexão</p>
                  </div>
                  
                  <div className="qr-actions">
                    <Button 
                      label="🔄 Atualizar QR" 
                      handleClick={generateQR}
                      color="primary"
                    />
                    <Button 
                      label="✓ Verificar Conexão" 
                      handleClick={refreshStatus}
                      color="success"
                    />
                  </div>
                </div>
              )}

              {connectionStatus === 'connected' && (
                <div className="success-step">
                  <div className="success-celebration">
                    <div className="success-animation">
                      <div className="success-icon-wrapper">
                        <img src={SuccessIcon} alt="Success" className="success-icon" />
                        <div className="celebration-particles">
                          <span>🎉</span><span>✨</span><span>🌟</span>
                        </div>
                      </div>
                    </div>
                    <h3>🎊 Parabéns! Tudo conectado!</h3>
                  </div>
                  <div className="success-content">
                    <p>🌟 <strong>Perfeito!</strong> Seu WhatsApp está conectado e funcionando perfeitamente na PlayBPO.</p>
                    <div className="success-features">
                      <div className="feature-item">
                        <span className="feature-icon">📱</span>
                        <span>Mensagens sendo recebidas</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">🔄</span>
                        <span>Sincronização ativa</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">💬</span>
                        <span>Pronto para conversar</span>
                      </div>
                    </div>
                    <div className="instance-info">
                      <p>📋 <span className="instance-label">Sua instância:</span> <strong>{instanceName}</strong></p>
                    </div>
                    <div className="success-next-steps">
                      <p>🎯 <strong>Próximos passos:</strong> Agora você pode começar a usar todos os recursos de comunicação!</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
    </ModalPanel>
  )
}

export default WhatsAppGate